import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Space, Typography, Button } from 'antd';
import { useScrollToTopWindow } from '../hooks/useScrollToTop';
import type { MenuProps } from 'antd';
import {
  HomeOutlined,
  CreditCardOutlined,
  DollarOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { useAgencyAuthStore } from '../stores/agencyAuthStore';
import { trpc, queryClient } from '../lib/api';
import { SERVICE_TITLE } from '@shared/config/service-config';
import { useResponsive, LAYOUT_CONSTANTS } from '../hooks/useResponsive';
import { MobileDrawerMenu } from '../components/layout/MobileDrawerMenu';
import { AppBreadcrumb, BreadcrumbProvider } from '../components/Breadcrumb';
import { DevRouteInfo } from '../components/common/DevRouteInfo';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

export function AgencyLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { agencyUser, clearAuth } = useAgencyAuthStore();
  const [collapsed, setCollapsed] = useState(false);

  // レスポンシブ対応
  const { isMobile, useMobileMenu } = useResponsive();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // ページ遷移時にスクロール位置をトップにリセット
  useScrollToTopWindow();

  const logoutMutation = trpc.agencyAuth.logout.useMutation({
    onSuccess: () => {
      // React Query のキャッシュを完全にクリア（重要：前のユーザーのデータを残さない）
      // invalidate() ではなく clear() を使用 - invalidate() は stale マークのみで実データは残る
      queryClient.clear();
      clearAuth();
      navigate('/agency/login');
    },
  });

  const handleLogout = () => {
    const token = localStorage.getItem('agencyToken');
    if (token) {
      logoutMutation.mutate({ token });
    } else {
      clearAuth();
      navigate('/agency/login');
    }
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'プロフィール',
      onClick: () => navigate('/agency/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'ログアウト',
      onClick: handleLogout,
    },
  ];

  const sidebarItems: MenuProps['items'] = [
    {
      key: '/agency/dashboard',
      icon: <HomeOutlined />,
      label: 'ダッシュボード',
    },
    {
      key: '/agency/coupons',
      icon: <CreditCardOutlined />,
      label: 'クーポン管理',
    },
    {
      key: '/agency/commissions',
      icon: <DollarOutlined />,
      label: '手数料・売上',
    },
    {
      key: '/agency/profile',
      icon: <UserOutlined />,
      label: 'プロフィール',
    },
  ];

  const selectedKey = sidebarItems
    ?.find((item) => typeof item?.key === 'string' && location.pathname.startsWith(item.key))
    ?.key as string | undefined;

  // メニュークリック時のハンドラー
  const handleMenuClick = (key: string) => {
    navigate(key);
  };

  // サイドバーの幅計算（モバイル時は0）
  const sidebarWidth = useMobileMenu ? 0 : (collapsed ? 80 : 240);
  const contentPadding = isMobile ? LAYOUT_CONSTANTS.CONTENT_PADDING_MOBILE : 24;
  const headerHeight = isMobile ? LAYOUT_CONSTANTS.HEADER_HEIGHT_MOBILE : LAYOUT_CONSTANTS.HEADER_HEIGHT;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* モバイル用ドロワーメニュー */}
      {useMobileMenu && (
        <MobileDrawerMenu
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          menuItems={sidebarItems}
          selectedKeys={selectedKey ? [selectedKey] : []}
          onMenuClick={handleMenuClick}
          title="代理店ポータル"
          subtitle="AGENCY PORTAL"
        />
      )}

      {/* デスクトップ用サイドバー */}
      {!useMobileMenu && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          width={240}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
          }}
        >
          <div
            style={{
              height: '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 16px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            {!collapsed && (
              <Text strong style={{ color: 'white', fontSize: '16px' }}>
                代理店ポータル
              </Text>
            )}
          </div>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={selectedKey ? [selectedKey] : []}
            items={sidebarItems}
            onClick={({ key }) => navigate(key)}
            style={{ borderRight: 0 }}
          />
        </Sider>
      )}

      <Layout style={{ marginLeft: sidebarWidth, transition: 'margin-left 0.2s' }}>
        <Header
          style={{
            background: '#fff',
            padding: `0 ${contentPadding}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
            position: 'fixed',
            top: 0,
            right: 0,
            left: sidebarWidth,
            height: headerHeight,
            lineHeight: `${headerHeight}px`,
            zIndex: 99,
            transition: 'left 0.2s',
          }}
        >
          <Space>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {/* モバイル: ハンバーガーメニュー */}
              {useMobileMenu ? (
                <MenuOutlined
                  style={{ fontSize: 20, cursor: 'pointer', padding: 8 }}
                  onClick={() => setMobileDrawerOpen(true)}
                />
              ) : (
                /* デスクトップ: サイドバー折り畳みトグル */
                <Button
                  type="text"
                  icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  onClick={() => setCollapsed(!collapsed)}
                  style={{
                    fontSize: '16px',
                    width: 48,
                    height: 48,
                  }}
                />
              )}
            </div>
            {/* モバイルではタイトルを短縮 */}
            {!isMobile && (
              <Text strong>{SERVICE_TITLE} - 代理店ポータル</Text>
            )}
          </Space>

          <Space>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} size={isMobile ? 'default' : 'default'} />
                {/* モバイルでは詳細情報を非表示 */}
                {!isMobile && (
                  <div>
                    <div>
                      <Text strong>{agencyUser?.name || 'ゲスト'}</Text>
                    </div>
                    {agencyUser?.companyName && (
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {agencyUser.companyName}
                        </Text>
                      </div>
                    )}
                  </div>
                )}
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content
          style={{
            marginTop: headerHeight,
            margin: `${headerHeight + contentPadding}px ${contentPadding}px ${contentPadding}px`,
            padding: contentPadding,
            minHeight: 280,
            background: '#fff',
            borderRadius: isMobile ? 4 : 8,
          }}
        >
          <BreadcrumbProvider>
            <AppBreadcrumb layoutType="agency" />
            <Outlet />
          </BreadcrumbProvider>
        </Content>

        {/* 開発環境でのみ表示されるファイルパス情報 */}
        <DevRouteInfo />
      </Layout>
    </Layout>
  );
}
